import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get("next") || location.state?.from || "/home";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate(redirect);
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="py-14 max-w-md mx-auto">
      <div className="overline mb-3">Access the hunt</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mb-8">
        Log <span style={{ color: "#76B82A" }}>in</span>.
      </h1>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <input
          className="loot-input"
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="login-email"
        />
        <input
          className="loot-input"
          placeholder="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="login-password"
        />
        {err && (
          <div className="text-[#E53935] font-mono text-sm" data-testid="login-error">
            {err}
          </div>
        )}
        <button type="submit" disabled={loading} className="btn-loot w-full" data-testid="login-submit">
          {loading ? "..." : "Enter the hunt"}
        </button>
      </form>
      <p className="mt-6 text-[#F5F2E8]/70 text-sm">
        New hunter?{" "}
        <Link to="/register" className="text-[#D7A62A] underline underline-offset-4" data-testid="register-link">
          Create account
        </Link>
      </p>
      <p className="mt-2 text-[#F5F2E8]/50 text-xs">
        <Link to="/forgot-password" className="underline underline-offset-4 hover:text-[#D7A62A]" data-testid="forgot-password-link">
          Forgot your password?
        </Link>
      </p>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({ email: "", password: "", name: "", username: "", city: "", ref: "" });
  const [termsOk, setTermsOk] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, ref: ref.toUpperCase() }));
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!termsOk) {
      setErr("You must agree to the Terms, Privacy Policy, and Safety Waiver to register.");
      return;
    }
    setLoading(true);
    const res = await register({ ...form, terms_accepted: true });
    setLoading(false);
    if (res.ok) {
      toast.success("You're in. Welcome to The Local Loot.");
      navigate("/home");
    } else {
      setErr(res.error);
    }
  };
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="py-14 max-w-md mx-auto">
      <div className="overline mb-3">Join the hunt</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mb-8">
        Create <span style={{ color: "#D7A62A" }}>hunter</span>.
      </h1>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <input className="loot-input" placeholder="Full name" value={form.name} onChange={onChange("name")} required data-testid="register-name" />
        <input
          className="loot-input"
          placeholder="Username (letters, numbers, _ or .)"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value.trim() })}
          required
          minLength={3}
          maxLength={30}
          pattern="[A-Za-z0-9_.]+"
          title="3–30 characters, letters/numbers/underscore/period only"
          data-testid="register-username"
        />
        <input className="loot-input" placeholder="Email" type="email" value={form.email} onChange={onChange("email")} required data-testid="register-email" />
        <input className="loot-input" placeholder="City (optional)" value={form.city} onChange={onChange("city")} data-testid="register-city" />
        <input
          className="loot-input"
          placeholder="Referral code (optional)"
          value={form.ref}
          onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })}
          data-testid="register-ref"
          maxLength={20}
        />
        {form.ref && (
          <p className="font-mono text-xs text-[#76B82A]">
            🎉 You'll get a +1 streak bonus for using a referral code.
          </p>
        )}
        <input className="loot-input" placeholder="Password (min 6 chars)" type="password" value={form.password} onChange={onChange("password")} required minLength={6} data-testid="register-password" />
        <label
          className="flex items-start gap-2 text-xs text-[#F5F2E8]/80 leading-relaxed cursor-pointer pt-2 border-t border-[#F5F2E8]/10"
          data-testid="register-terms-label"
        >
          <input
            type="checkbox"
            checked={termsOk}
            onChange={(e) => setTermsOk(e.target.checked)}
            required
            className="mt-0.5 shrink-0 accent-[#76B82A]"
            data-testid="register-terms-checkbox"
          />
          <span>
            I'm 18+ (or accompanied by a legal guardian) and I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-[#D7A62A] underline">Terms of Use</Link>,{" "}
            <Link to="/privacy" target="_blank" className="text-[#1e88e5] underline">Privacy Policy</Link>, and{" "}
            <Link to="/safety" target="_blank" className="text-[#E53935] underline">Safety Waiver</Link>.
            I understand hunting involves real-world risk and I participate at my own risk.
          </span>
        </label>
        {err && <div className="text-[#E53935] font-mono text-sm" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={loading || !termsOk} className="btn-loot w-full" data-testid="register-submit">
          {loading ? "..." : "Claim username"}
        </button>
      </form>
      <p className="mt-6 text-[#F5F2E8]/70 text-sm">
        Already a hunter?{" "}
        <Link to="/login" className="text-[#76B82A] underline underline-offset-4">
          Log in
        </Link>
      </p>
    </div>
  );
}


export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto py-16">
      <h1 className="font-display uppercase text-5xl tracking-tight mb-4">Forgot password?</h1>
      {sent ? (
        <div className="loot-card p-5 space-y-2" data-testid="forgot-success" style={{ borderColor: "#76B82A" }}>
          <div className="overline" style={{ color: "#76B82A" }}>Check your inbox</div>
          <p className="text-[#F5F2E8]/80 leading-relaxed text-sm">
            If <strong>{email}</strong> is a registered account, a password-reset link is on its way.
            The link expires in 1 hour. Check your spam folder if you don't see it within a couple minutes.
          </p>
          <Link to="/login" className="btn-ghost text-xs inline-block mt-2">← Back to login</Link>
        </div>
      ) : (
        <>
          <p className="text-[#F5F2E8]/70 text-sm mb-6">
            Enter your email and we'll send you a link to reset your password.
          </p>
          <form onSubmit={onSubmit} className="space-y-3">
            <input
              className="loot-input w-full"
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-email"
            />
            <button type="submit" disabled={loading || !email} className="btn-loot w-full" data-testid="forgot-submit">
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
          <p className="mt-6 text-[#F5F2E8]/50 text-xs">
            Remembered it?{" "}
            <Link to="/login" className="text-[#D7A62A] underline underline-offset-4">Log in</Link>
          </p>
        </>
      )}
    </div>
  );
}

export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — log in with your new password.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Reset failed. Your link may have expired.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto py-16">
        <h1 className="font-display uppercase text-4xl mb-3">Missing reset link</h1>
        <p className="text-[#F5F2E8]/70 text-sm">
          This page needs a valid reset token. Request a new link from the{" "}
          <Link to="/forgot-password" className="text-[#D7A62A] underline">forgot password</Link> page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-16">
      <h1 className="font-display uppercase text-5xl tracking-tight mb-4">Reset password</h1>
      {done ? (
        <div className="loot-card p-5" style={{ borderColor: "#76B82A" }} data-testid="reset-success">
          <div className="overline" style={{ color: "#76B82A" }}>Password updated</div>
          <p className="text-sm text-[#F5F2E8]/80 mt-2">Taking you to the login page…</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <input
            className="loot-input w-full"
            type="password"
            placeholder="New password (min 6 characters)"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            minLength={6}
            required
            data-testid="reset-pw"
          />
          <input
            className="loot-input w-full"
            type="password"
            placeholder="Confirm new password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={6}
            required
            data-testid="reset-pw2"
          />
          {err && <div className="text-[#E53935] font-mono text-sm" data-testid="reset-error">{err}</div>}
          <button type="submit" disabled={loading} className="btn-loot w-full" data-testid="reset-submit">
            {loading ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}
    </div>
  );
}
